import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { InputText } from 'primereact/inputtext';
import { InputSwitch } from 'primereact/inputswitch';
import { Button } from 'primereact/button';
import { TextEditor } from '../../components/TextEditor/TextEditor';
import BroadcastToggleField from '../../components/BroadcastToggleField/BroadcastToggleField';
import ChannelsField from '../../components/ChannelsField/ChannelsField';
import { useGetManagerGroupQuery } from '../../api/groupApi';
import {
  useGetManagerRequestsQuery,
  useRequestGroupUpdateMutation,
  useRequestGroupEnableMutation,
} from '../../api/requestApi';
import { useToast } from '../../context/ToastContext';
import { errMsg } from '../../utils/errMsg';
import EntityFormPage from '../../components/EntityFormPage/EntityFormPage';
import Badge from '../../components/Badge/Badge';
import TileImageField from '../../components/TileImageField/TileImageField';
import buttons from '../../styles/shared/buttons.module.css';

export default function GroupEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const showToast = useToast();

  const { data: group, isLoading, isError, error } = useGetManagerGroupQuery(id);
  const { data: pendingRequests } = useGetManagerRequestsQuery('pending');
  const [requestGroupEnable, { isLoading: isTogglingEnabled }] = useRequestGroupEnableMutation();
  const [requestGroupUpdate, { isLoading: isSubmittingRequest }] = useRequestGroupUpdateMutation();
  const [tileFile, setTileFile] = useState(null);

  const pendingRequest = useMemo(
    () => (pendingRequests ?? []).find((r) => r.group_id === id),
    [pendingRequests, id],
  );

  const { control, handleSubmit, reset, watch, setValue } = useForm({
    defaultValues: {
      name: '',
      terms: '',
      channels: [],
      enabled: false,
      geolocationRequired: false,
      broadcast: true,
    },
  });
  const broadcast = watch('broadcast');
  const watchedName = watch('name');

  useEffect(() => {
    if (group) {
      reset({
        name: group.name,
        terms: group.terms ?? '',
        channels: group.channels ?? [],
        enabled: group.enabled ?? false,
        geolocationRequired: group.geolocation_required ?? false,
        broadcast: group.broadcast ?? true,
      });
      setTileFile(null);
    }
  }, [group, reset]);

  useEffect(() => {
    if (isError) showToast('error', 'Groups', errMsg(error, 'Could not load group.'));
  }, [isError, error, showToast]);

  const onSave = async (data) => {
    const name = data.name.trim();
    const terms = data.terms.trim();
    const channelsChanged =
      data.channels.length !== (group.channels ?? []).length ||
      data.channels.some((c) => !(group.channels ?? []).includes(c));
    const geolocationChanged = data.geolocationRequired !== (group.geolocation_required ?? false);
    const broadcastChanged = data.broadcast !== (group.broadcast ?? true);
    const needsApproval =
      name !== group.name ||
      terms !== (group.terms ?? '') ||
      channelsChanged ||
      geolocationChanged ||
      broadcastChanged ||
      Boolean(tileFile);

    const enabledChanged = data.enabled !== group.enabled;

    try {
      if (enabledChanged) {
        await requestGroupEnable({ groupId: id, enabled: data.enabled }).unwrap();
      }
      if (needsApproval) {
        await requestGroupUpdate({
          groupId: id,
          name,
          tileFile,
          terms: terms || undefined,
          channels: data.channels,
          geolocation_required: data.geolocationRequired,
          broadcast: data.broadcast,
        }).unwrap();
      }
      showToast(
        'success',
        'Groups',
        needsApproval || enabledChanged ? 'Changes sent to admin for approval.' : 'No changes to submit.',
      );
      navigate('/manager/groups');
    } catch (err) {
      showToast('error', 'Groups', errMsg(err, 'Could not submit request.'));
    }
  };

  if (isLoading) return <p className="textMuted">Loading…</p>;
  if (!group) return null;

  return (
    <EntityFormPage title="Edit group" backTo="/manager/groups">
      <p className="textMuted mb-3">
        All changes, including enabling or disabling this group, require admin approval.
      </p>

      {pendingRequest && (
        <div className="mb-3">
          <Badge background="var(--color-badge-warn-bg)" color="var(--color-badge-warn-text)">
            pending change awaiting admin approval
          </Badge>
          <p className="textMuted mt-1" style={{ fontSize: 'var(--font-size-body-sm)' }}>
            {pendingRequest.type === 'group_enable'
              ? `enabled: ${pendingRequest.payload?.enabled}`
              : Object.entries(pendingRequest.payload ?? {})
                  .filter(([, v]) => v !== undefined && v !== null && v !== '')
 
                  .map(([k, v]) => `${k}: ${k === 'terms' ? 'updated' : Array.isArray(v) ? v.join(', ') : v}`)
                  .join(' · ') || 'No details.'}
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit(onSave)}>
        <Controller
          name="name"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <div className="field">
              <label htmlFor={field.name}>Name</label>
              <InputText id={field.name} className="w-full mb-3" {...field} />
            </div>
          )}
        />

        <TileImageField
          file={tileFile}
          onFileChange={setTileFile}
          fallbackUrl={group.tile_image_url}
          name={watchedName || group.name}
          onError={(msg) => showToast('error', 'Groups', msg)}
        />

        <ChannelsField control={control} broadcast={broadcast} required={false} />

        <BroadcastToggleField control={control} setValue={setValue} />

        <Controller
          name="enabled"
          control={control}
          render={({ field }) => (
            <div className="field flex align-items-center gap-2 mb-3">
              <InputSwitch
                inputId={field.name}
                checked={field.value}
                onChange={(e) => field.onChange(e.value)}
                disabled={pendingRequest?.type === 'group_enable'}
              />
              <label htmlFor={field.name} className="m-0">
                Enabled
              </label>
            </div>
          )}
        />

        <Controller
          name="terms"
          control={control}
          render={({ field }) => (
            <div className="field">
              <label htmlFor={field.name}>Terms &amp; conditions</label>
              <TextEditor value={field.value} onTextChange={(e) => field.onChange(e.htmlValue ?? '')} />
            </div>
          )}
        />

        <Controller
          name="geolocationRequired"
          control={control}
          render={({ field }) => (
            <div className="field flex align-items-center gap-2 mb-3">
              <InputSwitch
                inputId={field.name}
                checked={field.value}
                onChange={(e) => field.onChange(e.value)}
              />
              <label htmlFor={field.name} className="m-0">
                Require geolocation to subscribe
              </label>
            </div>
          )}
        />

        <Button
          type="submit"
          label="Save"
          className={buttons.saveBtn}
          loading={isTogglingEnabled || isSubmittingRequest}
        />
      </form>
    </EntityFormPage>
  );
}
